import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAwaitingPayment, shouldKeepPolling, POLL_INTERVAL_MS } from '@repo/shared-types';
import type { SubscriptionProrationBreakdown, SubscriptionOffer, PromoCodeCheck, PromotionSnapshot } from '@repo/shared-types';
import { useAuthStore } from '../stores/authStore';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface SubscriptionData {
  plan: {
    code: string;
    name: string;
    priceVnd: number;
    priceVndAnnual: number;
    maxStudents: number;
    maxTeacherSeats: number;
    maxAiMessagesPerMonth: number;
    maxReconcileTxPerMonth: number;
    showsWatermark: boolean;
    allowsExtras: boolean;
    allowsBookkeeping: boolean;
    allowsAiImport: boolean;
    allowsPayroll: boolean;
    /** The right to share an invoice over Zalo via the manual send-zalo
     *  button (Pro/Center) — renamed from allowsZnsAutoSend (THI-179): no
     *  auto-send path exists. The store that sells ZNS volume ships with
     *  THI-74; until then the compare table shows the row as "sắp ra mắt". */
    allowsZaloSend: boolean;
    /** The right to create a new branch (Center). Existing branches on a
     *  downgraded org keep listing and filtering either way — this flag only
     *  gates "Thêm chi nhánh". */
    allowsBranches: boolean;
    /** THI-105: the right to create a Sổ liên lạc (parent portal) link for a
     *  student (Pro/Center) — `useProgressLink` gates its GET on this so a
     *  FREE org's teacher opening the Tiến độ tab never fires a request that
     *  403s and auto-opens the shared upgrade dialog. */
    allowsProgressLink: boolean;
  };
  status: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';
  currentPeriodEnd: string;
  autoRenew: boolean;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  usage: { students: number; aiCreditsUsed: number; aiCreditsLimit: number };
  pendingInvoice: { id: string; amountVnd: number; dueAt: string; paymentRef: string; extendsTerm: boolean } | null;
  scheduledChange: { planCode: string; planName: string; startsAt: string } | null;
  nudge: {
    kind: 'TRIALING' | 'RENEWAL_DUE' | 'DOWNGRADED' | 'TRIAL_ENDED';
    dueAt: string;
    daysLeft: number;
  } | null;
  zaloSendEnabled: boolean;
  // THI-200: the best campaign this org may redeem right now, with no plan/cycle
  // filtering applied server-side — the dashboard filters by `planCodes`/`cycles`
  // itself per surface. Never carries a code (see toSubscriptionOffer on the API).
  offer: SubscriptionOffer | null;
}

export interface SubscriptionInvoiceDetail {
  id: string;
  status: 'PENDING' | 'PAID' | 'EXPIRED';
  amountVnd: number;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  paymentRef: string;
  periodStart: string;
  periodEnd: string;
  paidAt: string | null;
  plan: { code: string; name: string } | null;
  /** Present only when this invoice was born from a mid-cycle upgrade. */
  prorationBreakdown: SubscriptionProrationBreakdown | null;
  /** Does non-payment of THIS invoice drop the account to Free? */
  extendsTerm: boolean;
  qrUrl: string | null;
  bankInfo: { bin: string; accountNo: string; accountName: string } | null;
  /** THI-200: 0 when no promotion applied — used by Task 11's invoice detail. */
  discountVnd: number;
  promotionSnapshot: PromotionSnapshot | null;
}

function authHeaders(token: string | null): Record<string, string> {
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

export function useSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscription'],
    queryFn: async (): Promise<SubscriptionData> => {
      const res = await fetch(`${API}/subscription`, { headers: authHeaders(token) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed to load subscription');
      return json.data;
    },
    enabled: !!token,
  });
}

export interface UpgradeSubscriptionInput {
  planCode: 'PLUS' | 'PRO' | 'CENTER';
  billingCycle: 'MONTHLY' | 'ANNUAL';
  /** Sent only when a PromoCodeRow preview was accepted (THI-200). */
  promoCode?: string;
}

export function useUpgradeSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ planCode, billingCycle, promoCode }: UpgradeSubscriptionInput) => {
      const res = await fetch(`${API}/subscription/upgrade`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ planCode, billingCycle, promoCode }),
      });
      const json = await res.json();
      if (!json.success) {
        // THI-200: PROMO_<reason> errors carry a server-composed Vietnamese
        // message meant to sit inline near the code input, not as a generic
        // toast — stash the machine code on the thrown Error so callers can
        // branch on it without re-parsing the message string.
        const error = new Error(json.error?.message ?? 'Upgrade failed') as Error & { code?: string };
        error.code = json.error?.code;
        throw error;
      }
      return json.data as {
        subscriptionInvoiceId: string;
        amountVnd: number;
        paymentRef: string;
        qrUrl: string | null;
        bankInfo: { bin: string; accountNo: string; accountName: string };
        discountVnd: number;
        promotion: PromotionSnapshot | null;
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

/**
 * Preview a promo code before checkout — fired on the `Áp dụng` click, never
 * on keystrokes, so this is a mutation rather than a query (THI-200).
 * `retry: false`: a rejected code (invalid/expired/wrong plan) is not a
 * transient failure worth retrying, and retrying would flash the "checking…"
 * state repeatedly for what is already a definitive answer.
 */
export function usePromoCodeCheck() {
  const token = useAuthStore((s) => s.accessToken);
  return useMutation({
    mutationFn: async (input: {
      code: string;
      planCode: 'PLUS' | 'PRO' | 'CENTER';
      billingCycle: 'MONTHLY' | 'ANNUAL';
    }): Promise<PromoCodeCheck> => {
      const params = new URLSearchParams({ planCode: input.planCode, billingCycle: input.billingCycle });
      const res = await fetch(`${API}/subscription/promo/${encodeURIComponent(input.code)}?${params.toString()}`, {
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Promo check failed');
      return json.data as PromoCodeCheck;
    },
    retry: false,
  });
}

export function useCancelSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Cancel failed');
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useResumeSubscription() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/subscription/resume`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Resume failed');
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useSubscriptionInvoices() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscription', 'invoices'],
    queryFn: async () => {
      const res = await fetch(`${API}/subscription/invoices?limit=50`, {
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed');
      return json.data.items as Array<{
        id: string;
        status: string;
        amountVnd: number;
        paymentRef: string;
        periodStart: string;
        periodEnd: string;
        paidAt: string | null;
        createdAt: string;
      }>;
    },
    enabled: !!token,
  });
}

/**
 * Dev-only: mark a subscription invoice paid via the API's `devOnly`-gated
 * mock-pay route, so the trial→paid flow can be exercised while automatic
 * reconciliation of subscription payments isn't built yet. The route 404s
 * outside dev/staging; callers must also gate rendering on
 * `import.meta.env.DEV` so the control never even appears in a prod build.
 */
export function useMockPaySubscriptionInvoice() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API}/subscription/invoices/${id}/mock-pay`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Mock payment failed');
      return json.data;
    },
    // Broad prefix invalidation: this affects both the invoice detail (status
    // flips PENDING → PAID) and the top-level subscription (plan/period change).
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

/**
 * Abandon an unpaid subscription invoice. Nothing else lets a teacher clear one
 * they created by mistake — before this, it sat on the billing page until the
 * plan lapsed.
 */
export function useCancelSubscriptionInvoice() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API}/subscription/invoices/${id}/cancel`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Cancel failed');
      return json.data;
    },
    // Same prefix as mock-pay: the invoice detail and the subscription summary
    // (which carries pendingInvoice and the nudge) both change.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });
}

export function useSubscriptionInvoice(id: string | undefined) {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  // Captured once per mount: the polling window is measured from when the payer
  // opened the page, not from each refetch. A lazy useState initializer (rather
  // than `useRef(Date.now())`) keeps this pure under the react-compiler
  // purity rule — `useRef`'s argument is re-evaluated on every render even
  // though only the first call is kept.
  const [startedAt] = useState(() => Date.now());
  const wasAwaitingRef = useRef(false);

  return useQuery({
    queryKey: ['subscription', 'invoices', id],
    queryFn: async () => {
      const res = await fetch(`${API}/subscription/invoices/${id}`, {
        headers: authHeaders(token),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed');
      const data = json.data as { status: string };
      // The flip is observed here rather than in a component effect, so it fires
      // exactly once per transition regardless of how the page re-renders.
      if (wasAwaitingRef.current && !isAwaitingPayment(data.status)) {
        // The plan badge in the sidebar and the Gói & Thanh toán tab both read
        // this key; without it a teacher pays and still sees the old plan.
        qc.invalidateQueries({ queryKey: ['subscription'] });
      }
      wasAwaitingRef.current = isAwaitingPayment(data.status);
      return json.data as SubscriptionInvoiceDetail;
    },
    enabled: !!token && !!id,
    // A function, so TanStack asks after every fetch whether to keep going —
    // returning false is the whole teardown.
    refetchInterval: (query) => {
      const status = (query.state.data as { status?: string } | undefined)?.status;
      if (!status) return false;
      return shouldKeepPolling({ status, startedAt, now: Date.now() }) ? POLL_INTERVAL_MS : false;
    },
  });
}
